import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type TemplateCategory = 'FIXED' | 'FREE';

export type ContractTemplateSelectItemResult = {
  selectItemId: string;
  code: string;
  name: string;
  sortOrder: number;
  isDeleted: boolean;
};

export type ContractTemplateItemResult = {
  itemKey: string;
  label: string;
  formType: string;
  sortOrder: number;
  isDeleted: boolean;
  selectItems: ContractTemplateSelectItemResult[];
};

export type ContractTemplateGroupResult = {
  groupId: string;
  name: string;
  templateCategory: TemplateCategory;
  sortOrder: number;
  items: ContractTemplateItemResult[];
};

export type ContractTemplateUnassignedItemResult = {
  itemKey: string;
  label: string;
  formType: string;
  isDeleted: boolean;
  selectItems: ContractTemplateSelectItemResult[];
};

export type GetContractTemplateResponse = {
  results: ContractTemplateGroupResult[];
  unassignedItems: ContractTemplateUnassignedItemResult[];
};

export type GetContractTemplateParams = {
  visibleOnly?: boolean;
};

export type CreateContractTemplateGroupParams = {
  name: string;
};

export type UpdateContractTemplateGroupParams = {
  groupId: string;
  name: string;
};

export type GroupDisplayItem = {
  groupId: string;
  sortOrder: number;
};

export type UpdateContractTemplateGroupDisplayParams = {
  groups: GroupDisplayItem[];
};

export type CreateSelectItemInput = {
  code: string;
  name: string;
  sortOrder: number;
};

export type CreateContractTemplateItemParams = {
  itemKey: string;
  label: string;
  formType: string;
  groupId?: string | null;
  selectItems?: CreateSelectItemInput[];
};

export type UpdateSelectItemInput = {
  selectItemId?: string | null;
  code: string;
  name: string;
  sortOrder: number;
  isDeleted: boolean;
};

export type UpdateContractTemplateItemParams = {
  itemKey: string;
  label: string;
  selectItems?: UpdateSelectItemInput[];
};

export type DeleteContractTemplateItemParams = {
  itemKey: string;
};

export type ItemDisplayInput = {
  itemKey: string;
  groupId?: string | null;
  sortOrder: number;
};

export type UpdateContractTemplateItemDisplayParams = {
  items: ItemDisplayInput[];
};

// --- API関数 ---

/**
 * テンプレート一覧取得API
 * @param params.visibleOnly - 表示中のみ取得（任意, boolean, true: 表示中のみ, false/未指定: 全グループ）
 */
export const getContractTemplate = async (
  params: GetContractTemplateParams,
): Promise<GetContractTemplateResponse> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_CONTRACT_TEMPLATE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * グループ登録API
 * @param params.name - グループ名（必須, 最大200文字）
 */
export const createContractTemplateGroup = async (
  params: CreateContractTemplateGroupParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CONTRACT_TEMPLATE_GROUP, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.groupId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * グループ編集API
 * @param params.groupId - グループID（必須, 最大64文字）
 * @param params.name - グループ名（必須, 最大200文字）
 */
export const updateContractTemplateGroup = async (
  params: UpdateContractTemplateGroupParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_TEMPLATE_GROUP, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * グループ表示設定API
 * @param params.groups - 表示するグループの一覧（必須）
 * @param params.groups[].groupId - グループID（必須, 最大64文字）
 * @param params.groups[].sortOrder - 表示順（必須, 0以上）
 */
export const updateContractTemplateGroupDisplay = async (
  params: UpdateContractTemplateGroupDisplayParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_TEMPLATE_GROUP_DISPLAY, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * アイテム登録API
 * @param params.itemKey - 項目キー（必須, 最大100文字, 半角英数字 + - + _ のみ）
 * @param params.label - ラベル（必須, 最大200文字）
 * @param params.formType - フォーム種別（必須, 最大50文字, master_type.type_code: form_type）
 * @param params.groupId - グループID（任意, 最大64文字, 未所属の場合はnull）
 * @param params.selectItems - 選択肢リスト（任意, 選択系formTypeの場合に設定）
 * @param params.selectItems[].code - コード（必須, 最大50文字, 半角英数字 + - + _ のみ）
 * @param params.selectItems[].name - 名称（必須, 最大200文字）
 * @param params.selectItems[].sortOrder - 表示順（必須, 1以上）
 */
export const createContractTemplateItem = async (
  params: CreateContractTemplateItemParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CONTRACT_TEMPLATE_ITEM, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.itemKey;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * アイテム編集API
 * @param params.itemKey - 項目キー（必須, 最大100文字）
 * @param params.label - ラベル（必須, 最大200文字）
 * @param params.selectItems - 選択肢リスト（任意, 選択系formTypeの場合に設定）
 * @param params.selectItems[].selectItemId - 選択肢ID（任意, 最大64文字, 新規の場合はnull）
 * @param params.selectItems[].code - コード（必須, 最大50文字, 半角英数字 + - + _ のみ）
 * @param params.selectItems[].name - 名称（必須, 最大200文字）
 * @param params.selectItems[].sortOrder - 表示順（必須, 1以上）
 * @param params.selectItems[].isDeleted - 削除フラグ（必須, trueで論理削除）
 */
export const updateContractTemplateItem = async (
  params: UpdateContractTemplateItemParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_TEMPLATE_ITEM, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * アイテム削除API
 * @param params.itemKey - 項目キー（必須, 最大100文字）
 */
export const deleteContractTemplateItem = async (
  params: DeleteContractTemplateItemParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_CONTRACT_TEMPLATE_ITEM, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * アイテム表示設定API
 * @param params.items - 更新対象の全item（必須）
 * @param params.items[].itemKey - 項目キー（必須, 最大100文字）
 * @param params.items[].groupId - グループID（任意, 最大64文字, nullの場合は未所属に設定）
 * @param params.items[].sortOrder - 表示順（必須, グループ所属時は1以上、未所属時は-1）
 */
export const updateContractTemplateItemDisplay = async (
  params: UpdateContractTemplateItemDisplayParams,
): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_TEMPLATE_ITEM_DISPLAY, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
